import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  selectAllRecipes,
  selectRecipeViewMode,
  selectLastCookedByRecipe,
  selectCookCountByRecipe,
  selectRecipeSort,
  setRecipeViewMode,
  setRecipeSort,
  splitByCooked,
  useT,
  type RecipeSortOption,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { RecipeGrid } from "../components/RecipeGrid";
import { RecipeList } from "../components/RecipeList";
import { RecipeFilterBar, useRecipeFilter } from "../components/RecipeBarFilter";
import { RecipeViewSwitcher } from "../components/RecipeViewSwitcher";
import { RecipeSort } from "../components/RecipeSort";

export default function Library() {
  const t = useT();
  const dispatch = useAppDispatch();
  const recipes = useAppSelector(selectAllRecipes);
  const viewMode = useAppSelector(selectRecipeViewMode);
  const lastCookedAt = useAppSelector(selectLastCookedByRecipe);
  const cookCounts = useAppSelector(selectCookCountByRecipe);

  // Filter state + filtered list (owned by the hook; resets on unmount).
  const { filters, activeKeys, toggle, clear, filteredRecipes } =
    useRecipeFilter(recipes);
  // Shared sort preference (library/favourites/quick), persisted per device.
  const persistedSort = useAppSelector(selectRecipeSort) as RecipeSortOption;
  // A ?sort= deep link (home's "Cook it again → See all") sets a *transient* view
  // sort for this visit only — persisting it would surprise you with a subset the
  // next time you open Recipes. The override wins over the persisted default and is
  // reset when you leave the page (unmount). A manual sort change clears it + persists.
  const [viewSortOverride, setViewSortOverride] = useState<RecipeSortOption | null>(null);
  const sort = viewSortOverride ?? persistedSort;
  const setSort = (option: RecipeSortOption) => {
    setViewSortOverride(null);
    dispatch(setRecipeSort(option));
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) {
      setViewSortOverride(sortParam as RecipeSortOption);
      searchParams.delete("sort");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Split the filtered list into the sorted group and, for the cooked-only sorts,
  // the never-cooked remainder (ordered "Recently added") — so we show every recipe
  // under a "Never cooked" divider instead of silently hiding the uncooked ones.
  const { cooked, neverCooked } = useMemo(
    () => splitByCooked(filteredRecipes, sort, lastCookedAt, cookCounts),
    [filteredRecipes, sort, lastCookedAt, cookCounts],
  );
  const total = cooked.length + neverCooked.length;
  // Only show the divider when there's a genuine mix; if nothing's cooked yet we
  // just show all recipes as one group (no lone "Never cooked" heading).
  const showNeverCooked = cooked.length > 0 && neverCooked.length > 0;

  const emptyMessage = recipes.length
    ? t("library.emptyFilters")
    : t("library.emptyAddSidebar");

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display text-3xl font-extrabold text-ink">{t("nav.recipes")}</h1>
        <div className="flex items-center gap-2">
          <RecipeSort value={sort} onChange={setSort} />
          <RecipeViewSwitcher
            viewMode={viewMode}
            onSwitch={(mode) => dispatch(setRecipeViewMode(mode))}
          />
        </div>
      </div>

      <RecipeFilterBar
        filters={filters}
        activeKeys={activeKeys}
        onToggle={toggle}
        onClear={clear}
        totalCount={recipes.length}
      />

      <p className="text-sm text-ink-faint font-medium mb-4">
        {t("library.recipeCount", { count: total })}
      </p>

      {total === 0 ? (
        viewMode === "grid" ? (
          <RecipeGrid recipes={[]} emptyMessage={emptyMessage} />
        ) : (
          <RecipeList recipes={[]} emptyMessage={emptyMessage} />
        )
      ) : (
        <>
          {viewMode === "grid" ? (
            <RecipeGrid recipes={cooked.length ? cooked : neverCooked} />
          ) : (
            <RecipeList recipes={cooked.length ? cooked : neverCooked} />
          )}

          {showNeverCooked && (
            <>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint mt-10 mb-3">
                {t("library.neverCooked")}
              </h2>
              {viewMode === "grid" ? (
                <RecipeGrid recipes={neverCooked} />
              ) : (
                <RecipeList recipes={neverCooked} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
